import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, Plus, Sprout } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import FertiliserProductForm from '../../components/orchard/FertiliserProductForm';
import fertiliserService from '../../services/fertiliser';
import { errorMessage } from '../../utils/apiResponse';
import { cn } from '../../utils/cn';
import { getTodayInTz, useOrgTimezone } from '../../utils/timezone';
import { FORM_LABEL, addNutrients, gradeText, lineNutrients, round } from '../../utils/fertiliser';
import type { FertiliserPlan, FertiliserProduct, FertiliserStock, PlanList, StockLevel } from '../../types/fertiliser';

type Tab = 'plans' | 'products' | 'stock';
const TABS: { key: Tab; label: string }[] = [
  { key: 'plans', label: 'Plans' }, { key: 'products', label: 'Products' }, { key: 'stock', label: 'Stock' },
];
const seasonText = (y: number) => `${y}–${String(y + 1).slice(2)}`;
const inputCls = 'w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm';

function planTotals(p: FertiliserPlan) {
  return addNutrients(p.lines.map((l) => lineNutrients(l, l, p.area_hectares, p.tree_count).nutrients));
}

function PlansTab() {
  const navigate = useNavigate();
  const [data, setData] = useState<PlanList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [areaId, setAreaId] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fertiliserService.plans()
      .then((r) => { if (r.success && r.data) setData(r.data); })
      .catch((e) => setError(errorMessage(e, 'Could not load the plans')));
  }, []);

  const without = useMemo(() => {
    const has = new Set(data?.plans.map((p) => p.cultivar_area_id));
    return data?.areas.filter((a) => !has.has(a.cultivar_area_id)) ?? [];
  }, [data]);

  const start = async () => {
    if (!areaId) return;
    setCreating(true);
    try {
      const r = await fertiliserService.createPlan({ cultivar_area_id: Number(areaId), season_year: data?.season_year });
      if (r.success && r.data) navigate(`/orchard/fertiliser/plans/${r.data.id}`);
    } catch (e) {
      setError(errorMessage(e, 'Could not start the plan'));
    } finally {
      setCreating(false);
    }
  };

  if (error) return <p className="text-sm text-error-700">{error}</p>;
  if (!data) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-neutral-400" /></div>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-neutral-600">Season {seasonText(data.season_year)}. One plan per planting: what each hectare needs, and the products and dates that give it.</p>
      <Card>
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <label className="block text-sm flex-1 min-w-0 basis-56">
            <span className="text-neutral-700">Start a plan for</span>
            <select className={inputCls} value={areaId} onChange={(e) => setAreaId(e.target.value)}>
              <option value="">Choose a planting</option>
              {without.map((a) => (
                <option key={a.cultivar_area_id} value={a.cultivar_area_id}>
                  {a.farm_name} › {a.block_name ? `${a.block_name} › ` : ''}{a.cultivar_area_name}
                </option>
              ))}
            </select>
          </label>
          <Button variant="primary" isLoading={creating} disabled={!areaId} onClick={start}>
            <Plus className="h-4 w-4" />
            Start plan
          </Button>
        </CardContent>
      </Card>

      {data.plans.length === 0 && <p className="text-sm text-neutral-500">No plans for this season yet.</p>}
      <ul className="space-y-2">
        {data.plans.map((p) => {
          const t = planTotals(p);
          const jobs = p.lines.filter((l) => l.job_id).length;
          const done = p.lines.filter((l) => l.used_recorded_at).length;
          return (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => navigate(`/orchard/fertiliser/plans/${p.id}`)}
                className="w-full text-left bg-white rounded-xl border border-neutral-200 p-4 hover:border-primary-300 space-y-1"
              >
                <p className="text-sm font-medium text-neutral-900 break-words">
                  {p.farm_name} › {p.block_name ? `${p.block_name} › ` : ''}{p.cultivar_area_name}
                </p>
                <p className="text-xs text-neutral-600 flex flex-wrap gap-x-3 gap-y-1">
                  {(['N', 'P', 'K'] as const).map((n) => (
                    <span key={n}>
                      {n} <strong className="text-neutral-900">{round(t[n] ?? 0, 0)}</strong>
                      {p.targets[n] != null && <> of {round(p.targets[n], 0)}</>} kg/ha
                    </span>
                  ))}
                  <span className="text-neutral-500">{p.lines.length} applications · {jobs} jobs made · {done} recorded</span>
                </p>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ProductsTab() {
  const [products, setProducts] = useState<FertiliserProduct[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<FertiliserProduct | 'new' | null>(null);
  const [showRetired, setShowRetired] = useState(false);

  const load = useCallback(() => {
    fertiliserService.products(true)
      .then((r) => setProducts(r.success && r.data ? r.data : []))
      .catch((e) => setError(errorMessage(e, 'Could not load the fertilisers')));
  }, []);
  useEffect(load, [load]);

  const retire = async (p: FertiliserProduct) => {
    try {
      await fertiliserService.updateProduct(p.id, { ...p, is_active: !p.is_active });
      load();
    } catch (e) {
      setError(errorMessage(e, 'Could not change the fertiliser'));
    }
  };

  const shown = products?.filter((p) => showRetired || p.is_active) ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-neutral-600 min-w-0">Your organisation's fertilisers. Each is kept like a chemical: bought on a purchase order and held in a store.</p>
        {editing === null && (
          <Button variant="primary" size="sm" onClick={() => setEditing('new')}>
            <Plus className="h-4 w-4" />
            Add fertiliser
          </Button>
        )}
      </div>
      {error && <p className="text-sm text-error-700">{error}</p>}
      {editing && (
        <Card>
          <CardContent className="p-5">
            <FertiliserProductForm
              product={editing === 'new' ? null : editing}
              onCancel={() => setEditing(null)}
              onSaved={() => { setEditing(null); load(); }}
            />
          </CardContent>
        </Card>
      )}
      {!products && !error && <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-neutral-400" /></div>}
      {products && products.length === 0 && !editing && <p className="text-sm text-neutral-500">No fertilisers yet. Add the products you use, with the analysis from the bag or label.</p>}
      <ul className="space-y-2">
        {shown.map((p) => (
          <li key={p.id} className={cn('bg-white rounded-xl border border-neutral-200 p-4 space-y-1', !p.is_active && 'opacity-60')}>
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <p className="text-sm font-medium text-neutral-900 break-words min-w-0">
                {p.product_name}
                {p.manufacturer && <span className="text-neutral-500 font-normal"> · {p.manufacturer}</span>}
                {!p.is_active && <span className="text-neutral-500 font-normal"> · retired</span>}
              </p>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => setEditing(p)}>Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => retire(p)}>{p.is_active ? 'Retire' : 'Restore'}</Button>
              </div>
            </div>
            <p className="text-xs text-neutral-600 flex flex-wrap gap-x-3 gap-y-1">
              <span className="tabular-nums">{gradeText(p.nutrient_analysis)}</span>
              {p.fertiliser_form && <span>{FORM_LABEL[p.fertiliser_form]}</span>}
              <span>
                In stock: {p.stock.length ? p.stock.map((s) => `${round(s.quantity, 1)} ${s.unit}`).join(', ') : 'none'}
              </span>
            </p>
          </li>
        ))}
      </ul>
      {products && products.some((p) => !p.is_active) && (
        <button type="button" className="text-sm text-primary-600 hover:underline" onClick={() => setShowRetired((v) => !v)}>
          {showRetired ? 'Hide retired' : 'Show retired'}
        </button>
      )}
    </div>
  );
}

function StockTab() {
  const timezone = useOrgTimezone();
  const [stock, setStock] = useState<FertiliserStock | null>(null);
  const [products, setProducts] = useState<FertiliserProduct[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [mode, setMode] = useState<'received' | 'count'>('received');
  const [form, setForm] = useState({ chemical_id: '', location_id: '', quantity: '', unit: 'kg', supplier: '', date: getTodayInTz(timezone) });
  const [newStore, setNewStore] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    Promise.all([fertiliserService.stock(), fertiliserService.products()])
      .then(([s, p]) => {
        if (s.success && s.data) setStock(s.data);
        if (p.success && p.data) setProducts(p.data);
      })
      .catch((e) => setError(errorMessage(e, 'Could not load the stock')));
  }, []);
  useEffect(load, [load]);

  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setError(null);
    setMessage(null);
    const q = Number(form.quantity);
    if (!form.chemical_id || !form.location_id || !(q >= 0) || form.quantity.trim() === '') { setError('Choose the fertiliser and the store, and give the amount.'); return; }
    setBusy(true);
    try {
      if (mode === 'received') {
        if (!(q > 0)) { setError('The amount received is more than nothing.'); setBusy(false); return; }
        await fertiliserService.recordPurchase({
          chemical_id: Number(form.chemical_id), location_id: Number(form.location_id), quantity: q, unit: form.unit,
          supplier: form.supplier.trim() || null, purchase_date: form.date || null,
        });
        setMessage('Received into stock.');
      } else {
        await fertiliserService.recordCount({ chemical_id: Number(form.chemical_id), location_id: Number(form.location_id), counted_quantity: q, unit: form.unit });
        setMessage('Stock set to what was counted.');
      }
      setForm((f) => ({ ...f, quantity: '', supplier: '' }));
      load();
    } catch (e) {
      setError(errorMessage(e, 'Could not record it'));
    } finally {
      setBusy(false);
    }
  };

  const addStore = async () => {
    if (!newStore.trim()) return;
    try {
      await fertiliserService.createLocation(newStore.trim());
      setNewStore('');
      load();
    } catch (e) {
      setError(errorMessage(e, 'Could not add the store'));
    }
  };

  if (!stock && !error) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-neutral-400" /></div>;

  const byStore = new Map<string, StockLevel[]>();
  for (const l of stock?.levels ?? []) {
    if (!byStore.has(l.location_name)) byStore.set(l.location_name, []);
    byStore.get(l.location_name)?.push(l);
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-neutral-600">
        Fertiliser is held in the same stores as chemicals. A purchase order with a Chemical line naming the fertiliser
        adds it here when it is received; a job takes off what it used.
      </p>
      {error && <p className="text-sm text-error-700">{error}</p>}
      {message && <p className="text-sm text-green-700">{message}</p>}

      {stock && byStore.size === 0 && <p className="text-sm text-neutral-500">No fertiliser in stock.</p>}
      {[...byStore.entries()].map(([store, levels]) => (
        <Card key={store}>
          <CardContent className="p-4">
            <h4 className="text-sm font-semibold text-neutral-700 mb-1 break-words">{store}</h4>
            <ul className="divide-y divide-neutral-100">
              {levels.map((l) => (
                <li key={`${l.chemical_id}-${l.unit}`} className="py-1.5 flex flex-wrap justify-between gap-x-3 text-sm">
                  <span className="text-neutral-800 break-words min-w-0">{l.product_name}</span>
                  <span className={cn('tabular-nums', l.current_stock < 0 ? 'text-error-700' : 'text-neutral-900')}>
                    {round(l.current_stock, 1)} {l.unit}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ))}

      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex gap-1" role="radiogroup" aria-label="Record">
            {([['received', 'Received'], ['count', 'Counted']] as const).map(([k, label]) => (
              <button key={k} type="button" role="radio" aria-checked={mode === k} onClick={() => setMode(k)}
                className={cn('px-3 py-1.5 rounded-full border text-sm',
                  mode === k ? 'bg-primary-50 border-primary-300 text-primary-800' : 'border-neutral-200 text-neutral-600')}>
                {label}
              </button>
            ))}
          </div>
          {stock && stock.locations.length === 0 ? (
            <div className="flex flex-wrap items-end gap-2">
              <label className="block text-sm flex-1 min-w-0 basis-48">
                <span className="text-neutral-700">There is no store yet. Name one</span>
                <input type="text" className={inputCls} value={newStore} onChange={(e) => setNewStore(e.target.value)} placeholder="e.g. Fertiliser shed" />
              </label>
              <Button variant="outline" onClick={addStore}>Add store</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block text-sm">
                <span className="text-neutral-700">Fertiliser</span>
                <select className={inputCls} value={form.chemical_id} onChange={(e) => set('chemical_id', e.target.value)}>
                  <option value="">Choose</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.product_name}</option>)}
                </select>
              </label>
              <label className="block text-sm">
                <span className="text-neutral-700">Store</span>
                <select className={inputCls} value={form.location_id} onChange={(e) => set('location_id', e.target.value)}>
                  <option value="">Choose</option>
                  {stock?.locations.map((l) => <option key={l.id} value={l.id}>{l.name}{l.farm_name ? ` (${l.farm_name})` : ''}</option>)}
                </select>
              </label>
              <div className="grid grid-cols-[1fr_6rem] gap-2">
                <label className="block text-sm">
                  <span className="text-neutral-700">{mode === 'received' ? 'Amount received' : 'Amount counted'}</span>
                  <input type="text" inputMode="decimal" className={inputCls} value={form.quantity} onChange={(e) => set('quantity', e.target.value)} />
                </label>
                <label className="block text-sm">
                  <span className="text-neutral-700">Unit</span>
                  <select className={inputCls} value={form.unit} onChange={(e) => set('unit', e.target.value)}>
                    {['kg', 'g', 'L', 'mL'].map((u) => <option key={u}>{u}</option>)}
                  </select>
                </label>
              </div>
              {mode === 'received' && (
                <label className="block text-sm">
                  <span className="text-neutral-700">Supplier</span>
                  <input type="text" className={inputCls} value={form.supplier} onChange={(e) => set('supplier', e.target.value)} />
                </label>
              )}
            </div>
          )}
          {stock && stock.locations.length > 0 && (
            <div className="flex justify-end">
              <Button variant="primary" isLoading={busy} onClick={submit}>{mode === 'received' ? 'Record received' : 'Record count'}</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * /orchard/fertiliser: plans for the season, the organisation's fertiliser
 * products, and their stock. A plan opens on its own page.
 */
export default function Fertiliser() {
  const [params, setParams] = useSearchParams();
  const tab = (TABS.find((t) => t.key === params.get('tab'))?.key ?? 'plans') as Tab;

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Fertiliser"
        subtitle="Plans, products and stock"
        icon={<Sprout className="h-6 w-6 text-white" />}
        showBackButton
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
        <nav className="flex gap-1 border-b border-neutral-200" aria-label="Fertiliser">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              aria-current={tab === t.key ? 'page' : undefined}
              onClick={() => setParams(t.key === 'plans' ? {} : { tab: t.key })}
              className={cn('px-3 py-2 text-sm -mb-px border-b-2',
                tab === t.key ? 'border-primary-600 text-primary-700 font-medium' : 'border-transparent text-neutral-600')}
            >
              {t.label}
            </button>
          ))}
        </nav>
        {tab === 'plans' && <PlansTab />}
        {tab === 'products' && <ProductsTab />}
        {tab === 'stock' && <StockTab />}
      </div>
    </div>
  );
}
